/**
 * Emoji FAB — Material Design floating action button.
 *
 * States:
 *   ① Default:  Chat icon (blue FAB) — tap to open popup
 *   ② Expanded: × icon (gray FAB) + 4×2 emoji grid popup
 *   ③ Cooldown: Spinner icon (gray FAB, disabled) — 3s after send
 *
 * On emoji select → sends message, closes popup, starts cooldown.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { GameApi } from "../../services/game-api";
import { useEmojiMessages } from "../emoji-message-store";
import { sendEmojiMessage } from "../send-emoji-message";
import { AVAILABLE_EMOJIS, type EmojiId } from "../types";

interface EmojiFabProps {
  readonly gameApi: GameApi;
  readonly roomId: string;
  readonly displayName: string;
}

const FAB_SIZE = 48;
const FAB_RADIUS = 16;

const ChatIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2Z"
      fill="white"
    />
  </svg>
);

const Spinner = () => (
  <div
    style={{
      width: "24px",
      height: "24px",
      border: "3px solid rgba(255,255,255,0.3)",
      borderTopColor: "white",
      borderRadius: "50%",
      animation: "emoji-spin 0.8s linear infinite",
    }}
  />
);

export function EmojiFab({ gameApi, roomId, displayName }: EmojiFabProps) {
  const { cooldownUntil } = useEmojiMessages();
  const [isOpen, setIsOpen] = useState(false);
  const [isCoolingDown, setIsCoolingDown] = useState(false);

  useEffect(() => {
    if (cooldownUntil <= Date.now()) {
      setIsCoolingDown(false);
      return;
    }
    setIsCoolingDown(true);
    const remaining = cooldownUntil - Date.now();
    const timer = setTimeout(() => {
      setIsCoolingDown(false);
    }, remaining);
    return () => clearTimeout(timer);
  }, [cooldownUntil]);

  // Close popup on Escape key
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpenRef.current) {
        setIsOpen(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleFabClick = useCallback(() => {
    if (isCoolingDown) return;
    setIsOpen((prev) => !prev);
  }, [isCoolingDown]);

  const handleEmojiSelect = useCallback(
    async (emojiId: EmojiId) => {
      setIsOpen(false);
      await sendEmojiMessage(gameApi, roomId, emojiId, displayName);
    },
    [gameApi, roomId, displayName],
  );

  const fabStyle: React.CSSProperties = {
    position: "absolute",
    bottom: "72px",
    right: "16px",
    width: `${FAB_SIZE}px`,
    height: `${FAB_SIZE}px`,
    borderRadius: `${FAB_RADIUS}px`,
    backgroundColor: isCoolingDown
      ? "#888"
      : isOpen
        ? "#666"
        : "var(--cn-accent-blue, #1d4ed8)",
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "none",
    cursor: isCoolingDown ? "not-allowed" : "pointer",
    boxShadow:
      "0 3px 5px -1px rgba(0,0,0,0.2), 0 6px 10px rgba(0,0,0,0.14), 0 1px 18px rgba(0,0,0,0.12)",
    zIndex: 30,
    transition: "background-color 0.15s ease, transform 0.15s ease",
  };

  return (
    <>
      {/* Emoji popup (above FAB) */}
      {isOpen && (
        <div
          style={{
            position: "absolute",
            bottom: "128px",
            right: "16px",
            backgroundColor: "var(--cn-panel-bg, #fff)",
            border: "1px solid rgba(0,0,0,0.1)",
            borderRadius: "12px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
            padding: "10px",
            zIndex: 31,
          }}
        >
          <div
            style={{
              fontSize: "11px",
              fontWeight: 600,
              color: "#888",
              textAlign: "center",
              marginBottom: "8px",
            }}
          >
            이모지 선택
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: "6px",
            }}
          >
            {AVAILABLE_EMOJIS.map((entry) => (
              <button
                key={entry.id}
                type="button"
                data-testid={`emoji-btn-${entry.id}`}
                title={entry.label}
                onClick={() => void handleEmojiSelect(entry.id)}
                style={{
                  width: "44px",
                  height: "44px",
                  borderRadius: "8px",
                  border: "1px solid rgba(0,0,0,0.08)",
                  backgroundColor: "transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "22px",
                  cursor: "pointer",
                  transition:
                    "background-color 0.15s ease, transform 0.15s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "rgba(0,0,0,0.05)";
                  e.currentTarget.style.transform = "scale(1.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.transform = "scale(1)";
                }}
              >
                {entry.emoji}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Backdrop to close popup */}
      {isOpen && (
        <div
          role="presentation"
          onClick={() => setIsOpen(false)}
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 29,
          }}
        />
      )}

      {/* FAB button */}
      <button
        type="button"
        data-testid="emoji-fab"
        onClick={handleFabClick}
        disabled={isCoolingDown}
        style={fabStyle}
      >
        {isCoolingDown ? (
          <Spinner />
        ) : isOpen ? (
          <span style={{ fontSize: "20px", lineHeight: 1 }}>&times;</span>
        ) : (
          <ChatIcon />
        )}
      </button>
    </>
  );
}
